/**
 * Share to Eaves, the page's end of the stash (public/sw.js `takeShare`): a voice message shared from a chat app is
 * kept by the service worker in the `eaves-share-v1` cache, one clip under one key, until the page reads it. It is
 * another person's voice and lives only on this device: no timer removes it, but the next start, read or share after
 * `SHARED_STALE_MS` does. The first account to read it claims it (`x-shared-owner`); another account never sees it.
 * Every call is safe in a browser with no Cache API.
 */
import { SHARED_STALE_MS } from './shared-clip.generated';
import { currentOwner } from './session';

const CACHE = 'eaves-share-v1';
const KEY = '/__eaves-shared-clip';

export type Shared = { blob: Blob; at: number } | { refused: 'too-long' };

/**
 * When the page last dropped the stash: one made before then is gone already, even while the cache's delete is under
 * way (closing the sheet, then home settling, must not reopen it). A new share arrives by a page load, so this resets.
 */
let droppedAt = 0;

const open = () => (typeof caches === 'undefined' ? Promise.resolve(null) : caches.open(CACHE).catch(() => null));

/**
 * What the worker stashed, or null when nothing is there, it was dropped, it is older than 15 minutes or another
 * account claimed it (then it is deleted too). Read while `owner` is known, an unclaimed stash becomes that owner's;
 * read while nobody is (shared while signed out), it is still offered, to whoever signs in.
 */
export async function readShared(now = Date.now(), owner: string | null = currentOwner()): Promise<Shared | null> {
  try {
    const cache = await open();
    const res = await cache?.match(KEY);
    if (!cache || !res) return null;
    const at = Number(res.headers.get('x-shared-at'));
    if (droppedAt && at <= droppedAt) return null;
    const claimed = res.headers.get('x-shared-owner');
    if (!Number.isFinite(at) || at <= 0 || now - at > SHARED_STALE_MS || (claimed !== null && claimed !== owner)) {
      await cache.delete(KEY);
      return null;
    }
    if (owner !== null && claimed === null) {
      const headers = new Headers(res.headers);
      headers.set('x-shared-owner', owner);
      await cache.put(KEY, new Response(await res.clone().blob(), { headers }));
    }
    if (res.headers.get('x-shared-refused') === 'too-long') return { refused: 'too-long' };
    const blob = await res.blob();
    return blob.size ? { blob, at } : null;
  } catch {
    return null;
  }
}

/** Forget the stashed clip: from now on this page reads nothing, whether or not the delete has finished. */
export async function dropShared(now = Date.now()): Promise<void> {
  droppedAt = Math.max(droppedAt, now);
  try { await (await open())?.delete(KEY); } catch { /* nothing to drop */ }
}

/** At start: a stash nobody came for goes once it is stale. */
export async function sweepShared(now = Date.now()): Promise<void> {
  try {
    const cache = await open();
    const res = await cache?.match(KEY);
    if (!cache || !res) return;
    const at = Number(res.headers.get('x-shared-at'));
    if (!Number.isFinite(at) || now - at > SHARED_STALE_MS) await cache.delete(KEY);
  } catch { /* nothing to sweep */ }
}

/** True when a fresh stash waits for this owner (after sign-in, or home settling: go and look at it). */
export async function hasFreshShared(now = Date.now(), owner: string | null = currentOwner()): Promise<boolean> {
  return (await readShared(now, owner)) !== null;
}

/**
 * The length of a clip the pages could not give (a type other than Ogg Opus), from the browser: decoded, else its
 * metadata. null when neither says (the server then decides from the file).
 */
export async function measureClip(blob: Blob): Promise<number | null> {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctx) {
      const ctx = new Ctx();
      try {
        const d = (await ctx.decodeAudioData(await blob.arrayBuffer())).duration;
        if (Number.isFinite(d) && d > 0) return d;
      } finally { void ctx.close().catch(() => {}); }
    }
  } catch { /* try the element */ }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const a = new Audio();
    const done = (v: number | null) => { clearTimeout(timer); a.removeAttribute('src'); URL.revokeObjectURL(url); resolve(v); };
    const timer = setTimeout(() => done(null), 3000);
    a.preload = 'metadata';
    a.onloadedmetadata = () => done(Number.isFinite(a.duration) && a.duration > 0 ? a.duration : null);
    a.onerror = () => done(null);
    a.src = url;
  });
}
